/**
 * WebUSB ESC/POS thermal printer support.
 * Works in Chrome/Edge over HTTPS. Falls back gracefully if API not available.
 */

const VENDOR_IDS_HINT = [0x04b8, 0x0416, 0x0fe6, 0x0519, 0x067b, 0x1fc9, 0x28e9];
// Epson, Winbond, ICS, Star, Prolific, NXP, GD — common thermal-printer vendors

export function isWebUsbSupported() {
  return typeof navigator !== "undefined" && !!navigator.usb;
}

function b64ToBytes(b64) {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

let cachedDevice = null;

async function pickDevice() {
  if (cachedDevice && cachedDevice.opened) return cachedDevice;
  // Try previously paired
  try {
    const paired = await navigator.usb.getDevices();
    if (paired.length > 0) {
      cachedDevice = paired[0];
      return cachedDevice;
    }
  } catch (e) {
    if (process.env.NODE_ENV === "development") {
      // eslint-disable-next-line no-console
      console.warn("[escpos] getDevices failed", e);
    }
  }
  // Ask user (must be inside a user gesture)
  const device = await navigator.usb.requestDevice({ filters: VENDOR_IDS_HINT.map((id) => ({ vendorId: id })) });
  cachedDevice = device;
  return device;
}

async function openInterface(device) {
  if (!device.opened) await device.open();
  if (device.configuration === null) await device.selectConfiguration(1);
  // Find first interface with a bulk OUT endpoint
  for (const cfg of device.configurations) {
    for (const iface of cfg.interfaces) {
      for (const alt of iface.alternates) {
        const ep = alt.endpoints.find((e) => e.direction === "out" && e.type === "bulk");
        if (ep) {
          try {
            await device.claimInterface(iface.interfaceNumber);
          } catch (e) {
            if (process.env.NODE_ENV === "development") {
              // eslint-disable-next-line no-console
              console.warn("[escpos] claimInterface failed", e);
            }
          }
          return { interfaceNumber: iface.interfaceNumber, endpointNumber: ep.endpointNumber };
        }
      }
    }
  }
  throw new Error("Aucun endpoint bulk OUT trouvé sur l'imprimante");
}

export async function printEscposBytes(bytesB64) {
  if (!isWebUsbSupported()) throw new Error("WebUSB non supporté par ce navigateur (utilisez Chrome/Edge sur HTTPS)");
  const device = await pickDevice();
  const { endpointNumber } = await openInterface(device);
  const bytes = b64ToBytes(bytesB64);
  // Chunk if too large
  const CHUNK = 4096;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    const slice = bytes.slice(i, i + CHUNK);
    await device.transferOut(endpointNumber, slice);
  }
  return { ok: true, bytes: bytes.length };
}
